import { useExperimentTreatment } from '@ms/yammer-experiments-context';
import type { ITheme } from '@ms/yammer-web-core-ui/dist/util/theme';
import { loadCreateCoreUIV8ThemeFromV9 } from '@ms/yammer-web-core-ui/dist/util/theme';
import type { CoreUITokenOverrides, Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { useFluent9Theme } from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { useEffect, useState } from 'react';

import { useBrandTheme } from '../store';
import { isDefaultTheme } from '../theme';

type CurrentActiveThemeV9 = Theme | undefined;
type UseGetActiveNetworkBrandTheme = (
  fallbackTheme: ITheme,
  isUserTheme?: boolean
) => {
  readonly isCustomThemingEnabled: boolean;
  readonly currentActiveTheme: ITheme;
  readonly currentActiveThemeCoreUITokenOverrides: CoreUITokenOverrides;
  readonly currentActiveThemeV9: CurrentActiveThemeV9;
};

const emptyCoreUITokenOverrides: CoreUITokenOverrides = {};

interface ThemePrecedence {
  readonly isCustomThemingEnabled: boolean;
  readonly isDefaultNetworkBrandTheme: boolean;
  readonly isUserTheme: boolean;
}

const shouldApplyNetworkBrandTheme = ({
  isCustomThemingEnabled,
  isDefaultNetworkBrandTheme,
  isUserTheme,
}: ThemePrecedence): boolean => {
  if (isCustomThemingEnabled) {
    return true;
  }

  return !isUserTheme && isDefaultNetworkBrandTheme;
};

export const useGetActiveNetworkBrandTheme: UseGetActiveNetworkBrandTheme = (fallbackTheme, isUserTheme = false) => {
  const isCustomThemingEnabled = useExperimentTreatment('EngageCustomBrandingTheming') === true;
  const currentNetworkBrandTheme = useBrandTheme();
  const fallbackV9Theme = useFluent9Theme(fallbackTheme);
  const [currentActiveTheme, setCurrentActiveTheme] = useState<ITheme>(fallbackTheme);
  const [currentActiveThemeCoreUITokenOverrides, setCurrentActiveThemeCoreUITokenOverrides] =
    useState<CoreUITokenOverrides>(emptyCoreUITokenOverrides);
  const [currentActiveV9Theme, setCurrentActiveV9Theme] = useState<CurrentActiveThemeV9>(undefined);

  useEffect(() => {
    let isEffectActive = true;

    const setCurrentActiveThemeToDefault = () => {
      setCurrentActiveV9Theme(fallbackV9Theme);
      setCurrentActiveTheme(fallbackTheme);
      setCurrentActiveThemeCoreUITokenOverrides({
        colorThemeDark: fallbackV9Theme.colorBrandForeground1,
      });
    };

    /* istanbul ignore next */
    const updateTheme = async () => {
      const isDefaultNetworkBrandTheme = isDefaultTheme(currentNetworkBrandTheme);
      const shouldApplyCurrentNetworkBrandTheme = shouldApplyNetworkBrandTheme({
        isCustomThemingEnabled,
        isDefaultNetworkBrandTheme,
        isUserTheme,
      });

      if (!currentNetworkBrandTheme || !shouldApplyCurrentNetworkBrandTheme) {
        if (isEffectActive) {
          setCurrentActiveThemeToDefault();
        }

        return;
      }

      if (currentNetworkBrandTheme.tokensAndStyles) {
        try {
          const { createCoreUIV8ThemeFromV9 } = await loadCreateCoreUIV8ThemeFromV9();
          if (!isEffectActive) {
            return;
          }
          const isDarkMode = !!fallbackTheme?.isInverted;
          const v9Theme = !isDarkMode
            ? currentNetworkBrandTheme.tokensAndStyles.light
            : currentNetworkBrandTheme.tokensAndStyles.dark;
          const themeMode = !isDarkMode ? 'light' : 'dark';
          const newTheme = createCoreUIV8ThemeFromV9(v9Theme, themeMode);
          if (isEffectActive) {
            setCurrentActiveV9Theme(v9Theme);
            setCurrentActiveTheme(newTheme);
            setCurrentActiveThemeCoreUITokenOverrides({
              colorThemeDark: newTheme.palette.themeSecondary,
            });
          }
        } catch {
          if (isEffectActive) {
            setCurrentActiveThemeToDefault();
          }
        }
      } else {
        if (isEffectActive) {
          setCurrentActiveThemeToDefault();
        }
      }
    };

    updateTheme();

    return () => {
      isEffectActive = false;
    };
  }, [isCustomThemingEnabled, currentNetworkBrandTheme, fallbackTheme, fallbackV9Theme, isUserTheme]);

  return {
    isCustomThemingEnabled,
    currentActiveTheme,
    currentActiveThemeCoreUITokenOverrides,
    currentActiveThemeV9: currentActiveV9Theme,
  };
};
